import React, { forwardRef } from 'react';

const IUTPrint = forwardRef(({ transfer, copyType = 'ORIGINAL' }, ref) => {
  if (!transfer) return null;

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Pending Dispatch',
      in_transit: 'In Transit',
      received: 'Received',
      cancelled: 'Cancelled'
    };
    return labels[status] || status;
  };

  // Use items array if available, otherwise create from legacy fields
  const items = transfer.items && transfer.items.length > 0 
    ? transfer.items 
    : [{
        item_id: transfer.item_id,
        item_code: transfer.item_code,
        item_name: transfer.item_name,
        quantity: transfer.quantity,
        unit: transfer.unit,
        qty_ok: null,
        qty_not_ok: null
      }];

  // Calculate totals
  const totalQuantity = items.reduce((sum, item) => sum + (item.quantity || 0), 0);

  return (
    <div ref={ref} className="p-6 bg-white print:p-4" style={{ fontFamily: 'Arial, sans-serif', maxWidth: '800px', margin: '0 auto' }}>
      {/* Company Header */}
      <div className="text-center border-b-2 border-black pb-3 mb-4">
        <h1 className="text-xl font-bold">DREAMSPAN FURNITURE PVT LTD</h1>
        <p className="text-xs text-gray-600">Manufacturing Excellence Since 1995</p>
      </div>

      {/* Document Title */}
      <div className="text-center mb-4">
        <h2 className="text-lg font-bold border-2 border-black inline-block px-6 py-1">
          INTER-UNIT TRANSFER
        </h2>
        <p className="text-sm text-gray-500 mt-1">[{copyType} COPY]</p>
      </div>

      {/* Transfer Info */}
      <div className="flex justify-between mb-4 text-sm border border-gray-300 p-3 rounded">
        <div>
          <p><strong>IUT No.:</strong> <span className="font-mono">{transfer.transfer_number}</span></p>
          <p><strong>Status:</strong> {getStatusLabel(transfer.status)}</p>
          {transfer.gate_pass_number && (
            <p><strong>Gate Pass:</strong> <span className="font-mono">{transfer.gate_pass_number}</span></p>
          )}
        </div>
        <div className="text-right">
          <p><strong>Date:</strong> {formatDate(transfer.created_at)}</p>
          <p><strong>Time:</strong> {formatTime(transfer.created_at)}</p>
          {transfer.work_order_number && (
            <p><strong>Work Order:</strong> {transfer.work_order_number}</p>
          )}
        </div>
      </div>

      {/* From / To Section */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="border border-gray-300 p-3 rounded">
          <h3 className="font-bold text-sm uppercase mb-2 border-b pb-1">FROM</h3>
          <p className="text-lg font-bold">{transfer.from_unit_name}</p>
          <p className="text-sm text-gray-600">Code: {transfer.from_unit_code}</p>
        </div>
        <div className="border border-gray-300 p-3 rounded">
          <h3 className="font-bold text-sm uppercase mb-2 border-b pb-1">TO</h3>
          <p className="text-lg font-bold">{transfer.to_unit_name}</p>
          <p className="text-sm text-gray-600">Code: {transfer.to_unit_code}</p>
        </div>
      </div>

      {/* Items Table */}
      <div className="mb-4">
        <h3 className="font-bold text-sm uppercase mb-2">Items ({items.length})</h3>
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-gray-100">
              <th className="border border-gray-400 p-2 text-left w-10">#</th>
              <th className="border border-gray-400 p-2 text-left">Item Code</th>
              <th className="border border-gray-400 p-2 text-left">Item Name</th>
              <th className="border border-gray-400 p-2 text-right w-20">Qty</th>
              <th className="border border-gray-400 p-2 text-left w-16">Unit</th>
              {transfer.status === 'received' && (
                <>
                  <th className="border border-gray-400 p-2 text-right w-20">OK</th>
                  <th className="border border-gray-400 p-2 text-right w-20">NOT OK</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {items.map((item, idx) => (
              <tr key={item.item_id || idx}>
                <td className="border border-gray-400 p-2 text-center">{idx + 1}</td>
                <td className="border border-gray-400 p-2 font-mono text-xs">{item.item_code || '-'}</td>
                <td className="border border-gray-400 p-2">{item.item_name}</td>
                <td className="border border-gray-400 p-2 text-right font-medium">{item.quantity}</td>
                <td className="border border-gray-400 p-2">{item.unit}</td>
                {transfer.status === 'received' && (
                  <>
                    <td className="border border-gray-400 p-2 text-right text-green-700">{item.qty_ok ?? '-'}</td>
                    <td className="border border-gray-400 p-2 text-right text-red-600">{item.qty_not_ok ?? '-'}</td>
                  </>
                )}
              </tr>
            ))}
            {/* Total Row */}
            <tr className="bg-gray-50 font-bold">
              <td className="border border-gray-400 p-2" colSpan={3}>TOTAL</td>
              <td className="border border-gray-400 p-2 text-right">{totalQuantity}</td>
              <td className="border border-gray-400 p-2">-</td>
              {transfer.status === 'received' && (
                <>
                  <td className="border border-gray-400 p-2 text-right text-green-700">
                    {items.reduce((sum, i) => sum + (i.qty_ok || 0), 0)}
                  </td>
                  <td className="border border-gray-400 p-2 text-right text-red-600">
                    {items.reduce((sum, i) => sum + (i.qty_not_ok || 0), 0)}
                  </td>
                </>
              )}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Remarks */}
      {transfer.remarks && (
        <div className="mb-4 border border-gray-300 p-3 rounded">
          <h3 className="font-bold text-sm uppercase mb-1">Remarks</h3>
          <p className="text-sm">{transfer.remarks}</p>
        </div>
      )}

      {/* Transport Details (if dispatched) */}
      {(transfer.vehicle_no || transfer.driver_name) && (
        <div className="mb-4 border border-gray-300 p-3 rounded">
          <h3 className="font-bold text-sm uppercase mb-2">Transport Details</h3>
          <div className="grid grid-cols-3 gap-2 text-sm">
            <p><strong>Vehicle No.:</strong> {transfer.vehicle_no || '-'}</p>
            <p><strong>Driver:</strong> {transfer.driver_name || '-'}</p>
            <p><strong>Contact:</strong> {transfer.driver_contact || '-'}</p>
          </div>
        </div>
      )}

      {/* Signatures Section */}
      <div className="grid grid-cols-3 gap-4 text-sm mt-6 pt-4 border-t-2 border-gray-400">
        {/* Issued By */}
        <div className="text-center">
          <p className="font-bold mb-8">Issued By</p>
          <div className="border-t border-gray-400 pt-1">
            <p>{transfer.created_by_name}</p>
            <p className="text-xs text-gray-500">{formatDate(transfer.created_at)}</p>
          </div>
        </div>

        {/* Security Verification */}
        <div className="text-center">
          <p className="font-bold mb-8">Security Verification</p>
          <div className="border-t border-gray-400 pt-1">
            {transfer.dispatched_by_name ? (
              <>
                <p>{transfer.dispatched_by_name}</p>
                <p className="text-xs text-gray-500">{formatDate(transfer.dispatched_at)}</p>
              </>
            ) : (
              <p className="text-gray-400">________________</p>
            )}
          </div>
        </div>

        {/* Received By */}
        <div className="text-center">
          <p className="font-bold mb-8">Received By</p>
          <div className="border-t border-gray-400 pt-1">
            {transfer.received_by_name ? (
              <>
                <p>{transfer.received_by_name}</p>
                <p className="text-xs text-gray-500">{formatDate(transfer.received_at)}</p>
              </>
            ) : (
              <p className="text-gray-400">________________</p>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="text-center text-xs text-gray-400 mt-6 pt-2 border-t">
        <p>This document serves as the official Inter-Unit Transfer Challan</p>
        <p>Printed on: {new Date().toLocaleString('en-IN')}</p>
      </div>
    </div>
  );
});

IUTPrint.displayName = 'IUTPrint';

export default IUTPrint;
